import React, { useMemo, useState } from 'react';
import DataTable, { FILTER_TYPES } from '@/components/General/DataTable';
import { ColumnFilter } from '@/components/General/ColumnFilter';
import { DateRangeColumnFilter } from '@/components/General/DateRangeColumnFilter';
import generalStyles from '@/components/General/Styles.module.scss';
import ByzzerModal from '@/components/ByzzerCSRModal';
import styles from './TpaTable.module.scss';
import { format } from 'date-fns';
import API_STATES from '@/constants/StateConstants';
import { deleteTpa } from '@/services/api/company.service';
import { isoDateStringToDate } from '@/components/utils/utils';

export function formatTpaDate(value) {
    if (!value) {
        return '—';
    }
    const isoDate = String(value).split('T')[0];
    const parsedDate = isoDateStringToDate(isoDate);
    return parsedDate ? format(parsedDate, 'M/d/yyyy') : '—';
}

function TpaTable({ data, companyId, onEdit, onRefresh }) {
    const [tpaToDelete, setTpaToDelete] = useState(null);
    const [deleteApiState, setDeleteApiState] = useState(API_STATES.none);
    const [deleteApiError, setDeleteApiError] = useState('');

    async function handleConfirmDelete() {
        setDeleteApiState(API_STATES.loading);
        const response = await deleteTpa(companyId, tpaToDelete.id);
        if (response.status === API_STATES.success) {
            setDeleteApiState(API_STATES.none);
            setTpaToDelete(null);
            onRefresh();
        } else {
            setDeleteApiState(API_STATES.error);
            setDeleteApiError(response.error?.response?.data?.message ?? 'Failed to delete. Please try again.');
        }
    }

    const columns = useMemo(
        () => [
            {
                Header: 'Client Company Name',
                accessor: 'clientCompanyName',
                Filter: ColumnFilter,
            },
            {
                Header: 'TPA Link',
                accessor: 'tpaLink',
                disableSortBy: true,
                Filter: ColumnFilter,
                Cell: ({ value }) =>
                    value ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" className={styles.tpaLink}>
                            {value}
                        </a>
                    ) : (
                        '—'
                    ),
            },
            {
                Header: 'TPA Start Date',
                accessor: 'startDate',
                filter: FILTER_TYPES.dateRange,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => formatTpaDate(value),
            },
            {
                Header: 'TPA End Date',
                accessor: 'endDate',
                filter: FILTER_TYPES.dateRange,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => formatTpaDate(value),
            },
            {
                Header: 'Created Date',
                accessor: 'createdDtm',
                filter: FILTER_TYPES.dateRange,
                Filter: DateRangeColumnFilter,
                Cell: ({ value }) => (value ? format(new Date(value), 'M/d/yyyy') : '—'),
            },
            {
                Header: 'Created By',
                accessor: 'createdBy',
                Filter: ColumnFilter,
            },
            {
                id: 'actions',
                Header: 'Actions',
                disableFilters: true,
                disableSortBy: true,
                Cell: ({ row }) => (
                    <div className={styles.actions}>
                        <button
                            className={generalStyles.smallButton}
                            onClick={() => onEdit(row.original)}
                        >
                            Edit
                        </button>
                        <button
                            className={generalStyles.smallButton}
                            onClick={() => setTpaToDelete(row.original)}
                        >
                            Delete
                        </button>
                    </div>
                ),
            },
        ],
        [onEdit]
    );

    return (
        <>
            <DataTable
                columns={columns}
                data={data}
                noDataDescription="No TPA records found"
            />

            <ByzzerModal
                show={Boolean(tpaToDelete)}
                heading="Delete TPA"
                headerType="normal"
                size="small"
                onClose={() => { setTpaToDelete(null); setDeleteApiState(API_STATES.none); }}
                closeOnClickOutside={false}
            >
                <p>Are you sure you want to delete the TPA for <strong>{tpaToDelete?.clientCompanyName}</strong>?</p>
                {deleteApiState === API_STATES.error && (
                    <span className={generalStyles.errorMessage}>{deleteApiError}</span>
                )}
                <div className={styles.deleteFooter}>
                    <button
                        className={generalStyles.cancelOperationButton}
                        onClick={() => { setTpaToDelete(null); setDeleteApiState(API_STATES.none); }}
                        disabled={deleteApiState === API_STATES.loading}
                    >
                        Cancel
                    </button>
                    <button
                        className={generalStyles.confirmOperationButton}
                        onClick={handleConfirmDelete}
                        disabled={deleteApiState === API_STATES.loading}
                    >
                        {deleteApiState === API_STATES.loading ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </ByzzerModal>
        </>
    );
}

export default TpaTable;
